import { INSPECTION_CAMERA } from "@volca/ui/components/ui/empty-machine";
import { MachineEmptyState } from "@volca/ui/components/ui/machine-empty-state";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  DocumentListFilters,
  ListTableCard,
  ListTableEmpty,
  OwnerAndActionFilters,
  useApBusinessTimeZone,
} from "@/accounts-payable/common";
import { apToday, documentListEmptyMessage } from "@/accounts-payable/utils";
import { useGetQuickbooksConnection } from "@/business/quickbooks-query";
import {
  vendorCol,
  statusCol,
  invoiceNumberCol,
  jobCol,
  dateCol,
  addedCol,
  reviewCol,
  totalCol,
} from "./cols";
import { ExportInvoicesButton } from "./export-invoices-button";
import { useInvoicesTab } from "./use-invoices-tab";

const columns = [
  vendorCol,
  statusCol,
  invoiceNumberCol,
  jobCol,
  dateCol,
  addedCol,
  reviewCol,
  totalCol,
];

export const ApInvoicesTab = ({
  businessId,
  selectedId = null,
  onSelect,
}: {
  businessId: string | null;
  selectedId?: string | null;
  onSelect: (id: string) => void;
}) => {
  // A disconnected business keeps its bills, so only "never connected" hides
  // QuickBooks. The filter assumes it until the connection loads, so a saved
  // "Not in QuickBooks" applies from the first fetch.
  const { data: connection } = useGetQuickbooksConnection(businessId);
  const {
    query,
    viewportRef,
    onViewportScroll,
    awaitingPosition,
    restoring,
    statusFilters,
    filter,
    setFilter,
    search,
    setSearch,
    dayField,
    setDayField,
    dayRange,
    setDayRange,
    actionTypes,
    setActionTypes,
    assigneeTokens,
    setAssigneeTokens,
    debouncedSearch,
  } = useInvoicesTab(businessId, {
    hasQuickbooks: connection?.status !== "not_connected",
  });

  const today = apToday(useApBusinessTimeZone());
  const emptyMessage = documentListEmptyMessage("invoice", {
    range: dayRange,
    field: dayField,
    filtered:
      filter !== "all" ||
      debouncedSearch.trim() !== "" ||
      actionTypes.length > 0 ||
      assigneeTokens.length > 0,
    today,
  });
  const archiveEmpty =
    filter === "all" &&
    !debouncedSearch.trim() &&
    !dayRange &&
    actionTypes.length === 0 &&
    assigneeTokens.length === 0;

  return (
    <Stack align="start" gap={0} className="h-full min-h-0 w-full">
      <DocumentListFilters
        kind="invoice"
        statusFilters={statusFilters}
        status={filter}
        onStatusChange={setFilter}
        dayField={dayField}
        onDayFieldChange={setDayField}
        dayRange={dayRange}
        onDayRangeChange={setDayRange}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Vendor, invoice #, PO # or job #"
      />

      <ListTableCard
        label="Invoices"
        query={query}
        viewportRef={viewportRef}
        onViewportScroll={onViewportScroll}
        awaitingPosition={awaitingPosition}
        restoring={restoring}
        columns={columns}
        selectedId={selectedId}
        onSelect={onSelect}
        className="mt-0"
        toolbar={
          <Stack
            direction="row"
            gap={2}
            align="center"
            justify="between"
            wrap
            className="w-full"
          >
            <Stack direction="row" gap={2} align="center" wrap>
              <OwnerAndActionFilters
                businessId={businessId}
                actionTypes={actionTypes}
                onActionTypesChange={setActionTypes}
                assigneeTokens={assigneeTokens}
                onAssigneeTokensChange={setAssigneeTokens}
              />
            </Stack>
            <ExportInvoicesButton
              businessId={businessId}
              statusFilters={statusFilters}
              list={{
                status: filter,
                dayField,
                dayRange,
                assigneeTokens,
                search,
                actionTypes,
              }}
            />
          </Stack>
        }
        empty={
          archiveEmpty ? (
            <MachineEmptyState
              art={INSPECTION_CAMERA}
              layout="workspace"
              title={emptyMessage.title}
              description={emptyMessage.body}
            />
          ) : (
            <ListTableEmpty {...emptyMessage} />
          )
        }
        error={{
          title: "Couldn’t load invoices",
          body: "Please try again in a moment.",
        }}
      />
    </Stack>
  );
};
